import * as Sentry from '@sentry/angular';
import { browserSessionIntegration, browserTracingIntegration, replayIntegration } from '@sentry/angular';
import { initTracer, createSentryProvider, createSentryConfig } from '@kempoweroyj/ce-lib-rest-tracer';
import { AppConfig } from '../app/providers/config.service';
import {
    buildBackendApiTracePropagationTargets,
    shouldAttachTraceHeadersToBackendRequests,
} from '../app/providers/browser-trace-policy';
import { Profile } from '../app/providers/auth.service';
import { RELEASE } from '../version';
import { FEATURE_FLAGS } from '../app/services/feature-flags/feature-flag-definitions';

const DEVELOPMENT_ONLY_FLAG_KEYS = Object.values(FEATURE_FLAGS)
    .filter((f) => f.developmentOnly)
    .map((f) => f.key);

/** True when any `developmentOnly` feature flag is enabled in the given map. */
function developmentOnlyFlagsEnabled(flags: Record<string, boolean>): boolean {
    return DEVELOPMENT_ONLY_FLAG_KEYS.some((key) => flags[key] === true);
}

/**
 * Read current feature flag state from localStorage and return flat span
 * attributes namespaced under `user.session.*`.
 *
 * Called by `beforeSendSpan` on every span — reads directly from localStorage
 * so it always reflects the latest flag state without depending on Angular DI.
 */
function getFeatureFlagSpanAttributes(): Record<string, string | number | boolean> {
    try {
        const raw = localStorage.getItem('chargeye-feature-flags');
        if (!raw) return {};

        const flags = JSON.parse(raw) as Record<string, boolean>;
        const attrs: Record<string, string | number | boolean> = {};

        // Individual flags as user.session.feature_flags.<key>
        for (const [key, value] of Object.entries(flags)) {
            attrs[`user.session.feature_flags.${key}`] = value;
        }

        attrs['user.session.developer_mode'] = developmentOnlyFlagsEnabled(flags);

        return attrs;
    } catch {
        return {};
    }
}

/**
 * Global hook for AuthService to provide its profile to Sentry.
 * This allows late binding after Angular DI is ready.
 */
let authProfileGetter: (() => Profile | null) | null = null;

/**
 * Register a function that returns the current AuthService profile.
 * Should be called by AuthService during initialization.
 *
 * @param getter - Function that returns the current profile
 *
 * @example
 * // In AuthService constructor:
 * import { registerAuthProfileGetter } from '../sentry/init';
 *
 * constructor() {
 *     // Register getter so Sentry can access the profile
 *     registerAuthProfileGetter(() => this.profile);
 * }
 */
export function registerAuthProfileGetter(getter: (() => Profile | null) | null): void {
    authProfileGetter = getter;
}

function profileFromAuthStorage(): Profile | undefined {
    const sessionInfo = localStorage.getItem('kempower-csms-auth');
    if (!sessionInfo) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(sessionInfo);
        return {
            tenantId: parsed.tenantId,
            userRole: parsed.userRole,
            systemRoles: parsed.systemRoles || parsed.roles || [],
            impersonated: parsed.impersonated || false,
            sub: parsed.sub,
        } as Profile;
    } catch (err) {
        console.warn('Failed to parse kempower-csms-auth from localStorage', err);
        return undefined;
    }
}

function resolveSessionProfile(explicit?: Profile): Profile | undefined {
    if (explicit) {
        return explicit;
    }
    if (authProfileGetter) {
        try {
            return authProfileGetter() || undefined;
        } catch (err) {
            console.warn('Failed to get profile from AuthService', err);
        }
    }
    return profileFromAuthStorage();
}

function sessionPayloadFromProfile(p: Profile) {
    return {
        tenantId: p.tenantId,
        userRole: p.userRole,
        systemRoles: p.systemRoles || [],
        impersonated: p.impersonated || false,
    };
}

function userLocalStorageContext(): Record<string, unknown> {
    const local_storage: Record<string, unknown> = {};

    const orgInfo = localStorage.getItem('chargeye-organization-info');
    if (orgInfo) {
        try {
            local_storage.organization = JSON.parse(orgInfo);
        } catch {
            local_storage.organization = { value: orgInfo };
        }
    }

    const featureFlagsJson = localStorage.getItem('chargeye-feature-flags');
    if (featureFlagsJson) {
        try {
            const flags = JSON.parse(featureFlagsJson) as Record<string, boolean>;
            local_storage.feature_flags = flags;
            local_storage.developer_mode = developmentOnlyFlagsEnabled(flags);
        } catch {
            local_storage.feature_flags = { value: featureFlagsJson };
        }
    }

    return local_storage;
}

/**
 * Set consolidated user context in Sentry, including session and localStorage data.
 * This nests all context under "Contexts > User" in Sentry error reports for better organization.
 *
 * Context structure:
 * - id: User's sub (unique identifier)
 * - session: { tenantId, userRole, systemRoles, impersonated }
 * - local_storage: { organization, feature_flags, development_features }
 *
 * @param profile - Optional AuthService profile object (for direct calls)
 */
export function setUserContextInSentry(profile?: Profile): void {
    try {
        const existingUser = Sentry.getCurrentScope().getUser() || {};
        const sessionProfile = resolveSessionProfile(profile);
        const session = sessionProfile ? sessionPayloadFromProfile(sessionProfile) : undefined;
        const local_storage = userLocalStorageContext();

        const hasSession = !!session;
        const hasLocalStorage = Object.keys(local_storage).length > 0;
        if (!hasSession && !hasLocalStorage) {
            return;
        }

        Sentry.setUser({
            ...existingUser,
            id: sessionProfile?.sub || existingUser.id,
            ...(hasSession && { session }),
            ...(hasLocalStorage && { local_storage }),
        });
    } catch (err) {
        console.warn('Failed to set user context in Sentry', err);
    }
}

export function initSentry(config: AppConfig): void {
    const hostname = window.location.hostname;
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';

    // Browser-side distributed tracing (trace-header propagation + rest-tracer) is only
    // worthwhile when traces are actually sampled. When enabled it clones every outbound
    // API request and runs per-request tracer work; with sampling at 0 that is pure
    // overhead that can saturate the browser main thread (incident: dev.kempower.io).
    const browserTracingEnabled = isLocalHost || config.sentryTracesSampleRate > 0;

    // Only propagate trace headers to the REST API when tracing is enabled AND the SPA is
    // served from the same hostname as apiBaseUrl (or localhost). Vanity domains calling
    // kempower.io from chargeye.io would otherwise trigger CORS preflight without API
    // Allow-Headers updates.
    const attachTraceToBackend = browserTracingEnabled && shouldAttachTraceHeadersToBackendRequests(config.apiBaseUrl);
    const tracePropagationTargets = attachTraceToBackend
        ? buildBackendApiTracePropagationTargets(config.apiBaseUrl)
        : [];

    Sentry.init({
        dsn: config.sentryDsn,
        environment: config.envName,
        release: RELEASE,
        integrations: [
            browserTracingIntegration({
                // Filter out asset fetch spans (GET /assets or GET ./assets)
                shouldCreateSpanForRequest: (url) => {
                    return !url.includes('/assets/') && !url.includes('./assets/');
                },
                // Disable long animation frame spans
                enableLongAnimationFrame: false,
            }),
            browserSessionIntegration(),
            replayIntegration(),
        ],
        // Filter unwanted spans to reduce noise in traces.
        ignoreSpans: [
            // Filter all resource spans (scripts, CSS, links, images, etc.)
            { op: /^resource\./ },
            // Filter Zone spans except those with requestAnimationFrame
            { name: /^Zone(?!.*requestAnimationFrame)/ },
            // Filter long animation frame spans (backup in case enableLongAnimationFrame doesn't catch all)
            { op: 'ui.long-animation-frame' },
            // Filter http.client spans for asset requests (GET /assets or GET ./assets)
            { op: 'http.client', name: /^GET \.?\/assets/ },
        ],
        // IMPORTANT: tracePropagationTargets tells Sentry which URLs to send trace headers to
        // This is required for distributed tracing to work across frontend and backend
        tracePropagationTargets,
        tracesSampler: (samplingContext) => {
            const transactionName = samplingContext.transactionContext?.name || '';

            if (transactionName.toLowerCase().includes('n+1 api call')) {
                return 0.001;
            }

            if (isLocalHost) {
                // we want 100% trace sampling for local setups to simplify reproduction of errors
                return 1.0;
            }

            return config.sentryTracesSampleRate;
        },
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: config.sentryReplaySampleRate,
        ignoreErrors: ['ResizeObserver loop limit exceeded', /ERR_BLOCKED_BY_CLIENT/],
        beforeSend(event) {
            // Filter out errors from browser extensions
            if (event.exception) {
                const frames = event.exception.values?.[0]?.stacktrace?.frames;
                if (
                    frames &&
                    frames.some(
                        (frame) =>
                            frame.filename?.includes('extension://') ||
                            frame.filename?.includes('chrome-extension://') ||
                            frame.filename?.includes('moz-extension://')
                    )
                ) {
                    return null;
                }
            }

            return event;
        },
        beforeSendTransaction(event) {
            // Drop health check / polling transactions that don't provide value
            // but keep them if they represent errors
            const transactionName = event.transaction || '';
            const status = event.contexts?.trace?.status;
            const isSuccessful = status === 'ok';

            if (transactionName.includes('config.json') && isSuccessful) {
                return null;
            }

            return event;
        },
        // Inject feature flags and developer mode as span attributes on every
        // browser span.  beforeSendSpan receives the serialized span object and
        // can mutate it directly — unlike beforeSend/beforeSendTransaction where
        // scope modifications are too late for the current event.
        beforeSendSpan(span) {
            const ffAttrs = getFeatureFlagSpanAttributes();
            if (Object.keys(ffAttrs).length > 0) {
                span.data = { ...span.data, ...ffAttrs };
            }
            return span;
        },
    });

    // Initialize the rest-tracer for W3C Trace Context propagation only when browser
    // tracing is enabled — otherwise its per-request instrumentation adds overhead for
    // traces that are never sampled. Since Sentry is already initialized above with
    // browserTracingIntegration(), the SentryTracingProvider detects this and skips
    // re-initialization, preserving the browser tracing setup.
    if (browserTracingEnabled) {
        initTracer(
            createSentryProvider(),
            createSentryConfig({
                dsn: config.sentryDsn,
                environment: config.envName,
                serviceName: 'chargeye-frontend',
                release: RELEASE,
                // Use the same targets as the main Sentry.init() for consistency
                tracePropagationTargets,
                // Verbose per-request debug logging must never run on deployed hosts.
                debug: isLocalHost,
            })
        );
    }
}
